'use client'

import {ReactNode, forwardRef, InputHTMLAttributes, PropsWithChildren, useContext} from 'react'
import {cx} from '@local/utils/cx'
import TickSVG from '@local/assets/svgs/tick.svg'
import {FormContext} from '@local/stores/contexts'

export interface CheckboxProps extends InputHTMLAttributes<HTMLInputElement> {
	children: ReactNode,
	className?: string,
	checkboxClassName?: string,
	labelClassName?: string,
	required?: boolean,
	disabled?: boolean,
	active?: boolean,
	theme?: 'light' | 'dark',
	error?: boolean,
	asButton?: boolean,
	onClick?: () => void
}

export const Checkbox = forwardRef<HTMLInputElement, PropsWithChildren<CheckboxProps>>(({
	name,
	id,
	children,
	className,
	checkboxClassName,
	labelClassName,
	required = false,
	disabled = false,
	theme = 'light',
	error = false,
	...props
}: CheckboxProps, ref) => {
	const {hasValidation} = useContext(FormContext)

	return (
		<label
			className={cx(
				'relative inline-flex items-baseline gap-8',
				'font-light text-12 cursor-pointer text-black text-left',
				disabled && 'cursor-auto opacity-60 pointer-events-none',
				className,
			)}
			htmlFor={id || name}>
			<input
				ref={ref}
				type='checkbox'
				id={id || name}
				name={name || id}
				required={required}
				className={cx(
					'absolute pointer-events-none opacity-0 peer',
				)}
				{...props} />
			<i
				className={cx(
					'relative flex items-center justify-center rounded-xxs',
					theme === 'dark' ? 'text-white border-grey-2 peer-focus-visible:border-grey-1' : 'text-black peer-focus-visible:border-grey-3',
					'shrink-0 w-[16px] h-[16px] border peer-checked:[&>svg]:opacity-100',
					'transition-[background-color,border-color] duration-300 ease-quart-out',
					theme === 'light' ? 'border-grey-1 peer-checked:bg-black peer-checked:border-white/40' : 'peer-checked:bg-white',
					checkboxClassName,
					hasValidation && 'peer-invalid:border-red',

				)} aria-hidden>
				<TickSVG className={cx(
					'w-10 m-auto opacity-0',
					'transition-opacity duration-300 ease-quart-out',
					theme === 'dark' ? 'text-black' : 'text-white',
				)} />
			</i>
			<span className={cx(
				'transition-color duration-300 ease-quart-out',
				labelClassName,
				error && 'text-red',
				hasValidation && 'peer-invalid:text-red',
			)}>
				{children}
			</span>
		</label>
	)
})
